import { UserRound } from 'lucide-react';
import { CaptainBadge } from '../../components/CaptainBadge';
import EntityAvatar from '../../components/EntityAvatar';
import ScoreFloater from '../../components/ScoreFloater';
import StageMaskBackdrop from '../../components/StageMaskBackdrop';
import {
  ENTITY_TYPE_COLORS, ENTITY_TYPE_LABELS, STAGE_GRID_COLS, STAGE_GRID_ROWS,
  getGridPosition, getHeaderPosition,
} from './stageConfig';
import { formatFranc, formatFrancAdaptive } from '../../lib/utils';

/**
 * Arena (graphical) gameplay view — extracted verbatim from StageTab.js
 * (2026-02 StageTab gameplay sub-component split). Pure presentational:
 * renders the rectangular stage grid (category headers, faded placeholders,
 * entity sprites with captain crowns, and the real-time floaters overlay).
 * All scoring/state lives in StageTab and is passed down as props.
 */
const NO_BOOST = new Set();

// Shrink a long header ("INFLUENCER") so it fits its column on narrow phones;
// unsupported `cqw` → the 10px class size applies.
const headerFontSize = (label = '') =>
  `min(10px, calc((${(100 / STAGE_GRID_COLS).toFixed(2)}cqw - 10px) / ${(Math.max(label.length, 1) * 0.66).toFixed(2)}))`;

export const StageArenaView = ({
  stageConfig, columnPlan, slotsWithGrid, floatersByEntity,
  liveDaily, hasHugeFloater, onEntityClick, onDraft,
  boostedCategories = NO_BOOST,  // 2026-09-07 — "2x" badge on boosted headers
}) => {
  return (
    <div
      className="relative rounded-3xl overflow-hidden border border-border/60 bg-card"
      style={{ height: '480px', containerType: 'inline-size' }}
      data-testid="stage-arena"
    >
      {/* Arena background — custom image per stage style */}
      <div
        className="absolute inset-0 bg-cover"
        style={{
          backgroundImage: `url('${stageConfig.bg}')`,
          backgroundPosition: stageConfig.bgPosition,
          backgroundSize: 'cover',
          opacity: 0.35,
        }}
      />
      <div className="spotlight-overlay absolute inset-0" />

      {/* v2026-06 (parity #2) — ambient drifting theater masks behind the
          arena (decorative; sits below the sprites + floaters). */}
      <StageMaskBackdrop />


      {/* Column headers — anchored above each occupied column */}
      {columnPlan.map((col, colIdx) => {
        if (!col || !col.label) return null;  // mixed columns get no label
        const pos = getHeaderPosition(colIdx);
        return (
          <div
            key={`col-${colIdx}`}
            className="absolute pointer-events-none"
            style={{ ...pos, transform: 'translate(-50%, -50%)', zIndex: 2 }}
            data-testid={`stage-grid-header-${colIdx}-${col.label}`}
          >
            <span
              className="relative text-[10px] uppercase font-semibold tracking-normal sm:tracking-wider text-foreground/85 px-1 sm:px-2 py-0.5 rounded-full bg-background/55 backdrop-blur-sm whitespace-nowrap inline-flex items-center"
              style={{ borderLeft: `2px solid ${ENTITY_TYPE_COLORS[col.label]}`, fontSize: headerFontSize(ENTITY_TYPE_LABELS[col.label]) }}
            >
              {ENTITY_TYPE_LABELS[col.label]}
              {boostedCategories.has(col.label) && (
                <span
                  className="absolute -top-2.5 -right-2 text-[9px] font-black leading-none px-1 py-[1px] rounded bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground,0_0%_100%))]"
                  title="Booster active — 2x earnings"
                  data-testid={`stage-grid-boost-${col.label}`}
                >
                  2x
                </span>
              )}
            </span>
          </div>
        );
      })}

      {/* Empty state */}
      {slotsWithGrid.length === 0 && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <p className="text-muted-foreground text-sm mb-3">Your stage is empty</p>
          <p className="text-xs text-muted-foreground">Draft people from Explore to fill your team</p>
        </div>
      )}

      {/* ── Entity sprites layer ── */}
      {slotsWithGrid.map((slot) => {
        const entity = slot.entity;
        const rowIdx = Math.min(slot._row, STAGE_GRID_ROWS - 1);
        const pos = getGridPosition(slot._col, rowIdx);
        const typeColor = ENTITY_TYPE_COLORS[slot.entity_type] || '#888';

        // v5.7.98 — Placeholder branch. Faded silhouette in
        // the category colour with a generic person icon so
        // the user sees the target shape pre-draft.
        if (slot._isPlaceholder) {
          return (
            <div
              key={slot.entity_id}
              className="entity-sprite absolute flex flex-col items-center cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              role="button"
              tabIndex={0}
              aria-label={`Draft a ${ENTITY_TYPE_LABELS[slot.entity_type] || slot.entity_type}`}
              style={{ left: pos.left, top: pos.top, transform: 'translate(-50%, -50%)', zIndex: 4 }}
              data-testid={`stage-placeholder-${slot.entity_type}-${slot._col}-${slot._row}`}
              onClick={() => onDraft && onDraft()}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onDraft && onDraft(); } }}
            >
              <div
                className="rounded-full flex items-center justify-center transition-transform hover:scale-105"
                style={{
                  width: 50,
                  height: 50,
                  // Stronger fill since we no longer rely on
                  // opacity to convey "empty" — the solid
                  // dashed border + icon are the signal.
                  background: `${typeColor}55`,
                  border: `2px dashed ${typeColor}`,
                }}
              >
                <UserRound className="h-7 w-7" style={{ color: typeColor }} aria-hidden />
              </div>
              <div className="mt-1.5 w-[76px] sm:w-[84px] min-h-[27px] sm:min-h-[30px] flex items-start justify-center">
                {/* 2026-08-15 — category name instead of "empty": flow-fill
                    custom shapes can mix categories in one column, so each
                    cell must say what it's waiting for. */}
                <span className="text-[9px] sm:text-[10px] font-medium italic" style={{ color: typeColor }}>
                  {ENTITY_TYPE_LABELS[slot.entity_type] || 'empty'}
                </span>
              </div>
            </div>
          );
        }

        const flash = hasHugeFloater(slot.entity_id) ? '1' : undefined;
        const daily = liveDaily(slot);
        return (
          <div
            key={slot.entity_id}
            className="entity-sprite absolute flex flex-col items-center cursor-pointer rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            role="button"
            tabIndex={0}
            aria-label={`View ${entity?.name || 'person'} details`}
            style={{ left: pos.left, top: pos.top, transform: 'translate(-50%, -50%)', zIndex: 5 }}
            data-testid={`stage-entity-${slot.entity_id}`}
            data-flash={flash}
            onClick={() => onEntityClick && onEntityClick(slot.entity_id)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onEntityClick && onEntityClick(slot.entity_id); } }}
          >
            {/* Avatar wrapper — fixed dimensions so a captain
                ring/crown doesn't shift the row layout. v5.7.48:
                the captain marker is now a thick gold ring on
                the avatar's outer edge (was an inset 2-px ring
                that looked like an internal pattern) plus a
                slightly-tilted crown anchored to the top-right
                of the avatar with a small black "C" inside it. */}
            <div className="relative" style={{
              boxShadow: `0 0 ${Math.max(10, (entity?.stats?.form_score || 50) / 4)}px ${typeColor}70`,
              borderRadius: '50%',
            }}>
              <div
                className={slot.is_captain ? 'rounded-full' : ''}
                style={slot.is_captain ? {
                  // 3-px outer gold ring instead of an inset
                  // pattern — sits flush on the avatar circle.
                  boxShadow: '0 0 0 3px hsl(43, 74%, 52%)',
                  borderRadius: '50%',
                } : undefined}
                data-testid={slot.is_captain ? `stage-captain-ring-${slot.entity_id}` : undefined}
              >
                <EntityAvatar
                  entity={entity || { entity_type: slot.entity_type }}
                  size={50}
                  className={`sm:!w-14 sm:!h-14${entity?.metadata?.death_date ? ' grayscale opacity-70' : ''}`}
                />
              </div>
              {slot.is_captain && <CaptainBadge />}
            </div>

            {/* Fixed-height name container so every avatar's footer
                (badge) sits at the same horizontal line, regardless of
                whether the person has one name or two. */}
            <div
              className="mt-1.5 flex flex-col items-center justify-start text-center w-[76px] sm:w-[84px] min-h-[27px] sm:min-h-[30px]"
              data-testid={`stage-entity-name-${slot.entity_id}`}
            >
              {(() => {
                const full = entity?.name || 'Unknown';
                const parts = full.trim().split(/\s+/);
                const trunc = (s) => s.length > 13 ? s.slice(0, 12) + '…' : s;
                if (parts.length <= 1) {
                  return <span className="text-[10px] sm:text-[11px] font-semibold text-foreground/95 leading-tight">{trunc(full)}</span>;
                }
                const first = parts[0];
                const rest = parts.slice(1).join(' ');
                return (<>
                  <span className="text-[10px] sm:text-[11px] font-semibold text-foreground/95 leading-tight">{trunc(first)}</span>
                  <span className="text-[10px] sm:text-[11px] font-semibold text-foreground/95 leading-tight">{trunc(rest)}</span>
                </>);
              })()}
            </div>

            {/* Daily +₣ badge — pinned at a fixed offset below the
                (fixed-height) name container so it lines up across
                the whole row, no matter the name length. For
                deceased roster slots, show a small "RIP" pill in
                the same spot so the row keeps its visual height. */}
            <div className="mt-1 h-[16px] flex items-center justify-center">
              {entity?.metadata?.death_date ? (
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full tabular-nums whitespace-nowrap bg-muted/80 text-muted-foreground border border-border/60"
                  data-testid={`stage-rip-${slot.entity_id}`}
                  title={`Deceased ${entity.metadata.death_date}`}
                >
                  ✟ RIP
                </span>
              ) : daily > 0 ? (
                <span className="badge-positive text-[10px] px-1.5 py-0.5 rounded-full tabular-nums whitespace-nowrap">
                  +{formatFrancAdaptive(daily)}
                </span>
              ) : (
                /* 2026-07-21 (owner request — mobile parity): when nothing
                   was earned today, show the person's current price in a
                   muted pill (mobile's arena has always done this). */
                <span
                  className="text-[10px] px-1.5 py-0.5 rounded-full tabular-nums whitespace-nowrap bg-muted/70 text-muted-foreground border border-border/50"
                  data-testid={`stage-price-${slot.entity_id}`}
                >
                  {formatFranc(entity?.pricing?.price)}
                </span>
              )}
            </div>
          </div>
        );
      })}

      {/* ── Floaters overlay layer — renders ABOVE all sprites so a
          score popup is never visually covered by a neighbouring
          avatar's tile. Each floater is positioned at its owning
          entity's coordinates (same getGridPosition math). ── */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ zIndex: 60 }}
        data-testid="stage-floaters-layer"
      >
        {slotsWithGrid.map((slot) => {
          const rowIdx = Math.min(slot._row, STAGE_GRID_ROWS - 1);
          const pos = getGridPosition(slot._col, rowIdx);
          const myFloaters = floatersByEntity[slot.entity_id] || [];
          if (myFloaters.length === 0) return null;
          return (
            <div
              key={`floater-${slot.entity_id}`}
              className="absolute"
              // Anchor just above where the avatar sits in the sprite layer
              style={{ left: pos.left, top: pos.top, transform: 'translate(-50%, -50%)' }}
            >
              <div className="relative" style={{ width: 56, height: 56 }}>
                {myFloaters.map((f) => (
                  <ScoreFloater key={f.id} points={f.points} source={f.source} event_type={f.event_type} emoji={f.emoji} visual={f.visual} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
